import { Component, OnInit } from '@angular/core';
import { forkJoin } from 'rxjs';
import { ApiService } from '../services/api.service';
import { LanguageService, AppLanguage } from '../services/language.service';

type L = { en: string; so: string };
type Coverage = 'full' | 'partial' | 'none';
type Tier = 'admin' | 'management' | 'field' | 'service' | 'readonly';

interface DomainDef {
  key: string;
  label: L;
}

interface TierMeta {
  key: Tier;
  label: L;
  blurb: L;
}

interface RoleDef {
  /** matched case-insensitively against role.roleName from the API */
  key: string;
  title: L;
  summary: L;
  capabilities: L[];
  /** what this role explicitly cannot do — shown under "Limits" */
  limits: L;
  tier: Tier;
  coverage: { [domain: string]: Coverage };
}

interface RoleRow extends RoleDef {
  memberCount: number;
  members: string[];
}

/**
 * Org-wide roles & capabilities reference (HR menu — sysadmin / supersysadmin only).
 *
 * Role descriptions are kept client-side because the `roles` table stores only
 * role_id + role_name. Capabilities below were verified against route guards,
 * menu *ngIf gates, backend allowedRoles lists and the branch-scope filter.
 *
 * Bilingual: every display string is { en, so } and switched via LanguageService
 * (the same service the header language dropdown uses), so the toggle here stays
 * in sync with the rest of the app and persists across sessions.
 */
@Component({
  selector: 'app-hr-roles-guide',
  templateUrl: './hr-roles-guide.component.html',
  styleUrls: ['./hr-roles-guide.component.scss'],
})
export class HrRolesGuideComponent implements OnInit {
  loading = true;
  loadError = '';
  rows: RoleRow[] = [];
  totalEmployees = 0;

  // ---- static UI strings -------------------------------------------------
  readonly ui: { [k: string]: L } = {
    title:        { en: 'Roles & Capabilities', so: 'Doorka & Awoodaha' },
    subtitle:     { en: 'What each role can do across the web application and mobile app',
                    so: 'Waxa doorka kastaa sameyn karo ee web-ka iyo app-ka mobilada' },
    statRoles:    { en: 'Roles', so: 'Doorka' },
    statScope:    { en: 'Employees in scope', so: 'Shaqaalaha aragtidaada' },
    scopeNoteSuper: {
      en: 'Member lists reflect the employees visible to your account — supersysadmin sees all branches (or the branch selected in the header).',
      so: 'Liisaska xubnahu waxay muujinayaan shaqaalaha aad arki karto — supersysadmin wuxuu arkaa dhammaan laamaha (ama laanta aad madaxa ka dooratay).' },
    scopeNoteBranch: {
      en: 'Member lists reflect the employees visible to your account — restricted to your own branch.',
      so: 'Liisaska xubnahu waxay muujinayaan shaqaalaha aad arki karto — waxaa lagu xaddiday laantaada.' },
    loading:      { en: 'Loading roles…', so: 'Doorka ayaa la soo dhigayaa…' },
    matrixTitle:  { en: 'Capability Matrix', so: 'Jadwalka Awoodaha' },
    legendFull:   { en: 'full access', so: 'awood buuxda' },
    legendPart:   { en: 'partial / gated by admin toggles or extra roles', so: 'qeyb ah / ku xidhan dammayaasha maamulka ama doorka kale' },
    legendNone:   { en: 'none', so: 'majiro' },
    roleColumn:   { en: 'Role', so: 'Doorka' },
    hierarchyTitle: { en: 'Role Hierarchy', so: 'Heerarka Doorka' },
    membersLabel: { en: 'Holders (your scope)', so: 'Kuwa haysta (aragtidaada)' },
    memberOne:    { en: 'member', so: 'xubin' },
    memberMany:   { en: 'members', so: 'xubno' },
    limitsLabel:  { en: 'Cannot', so: 'Ma awoodo' },
  };

  readonly domains: DomainDef[] = [
    { key: 'web',      label: { en: 'Web Menus & Dashboards', so: 'Bogagga Webka & Dashboard' } },
    { key: 'billing',  label: { en: 'Billing & Meters',       so: 'Biilashada & Mitirrada' } },
    { key: 'payments', label: { en: 'Payments & Banking',     so: 'Lacag-bixinta & Bangiyada' } },
    { key: 'customers',label: { en: 'Customer Records',       so: 'Diiwaanka Macaamiisha' } },
    { key: 'expenses', label: { en: 'Expenses',               so: 'Kharashaadka' } },
    { key: 'hr',       label: { en: 'HR & Payroll',           so: 'Shaqaalaha & Mushaharka' } },
    { key: 'assets',   label: { en: 'Assets & Inventory',     so: 'Hantida & Bakhaarka' } },
    { key: 'sysadmin', label: { en: 'System Administration',  so: 'Maamulka Nidaamka' } },
  ];

  readonly tierMeta: TierMeta[] = [
    { key: 'admin', label: { en: 'Administration', so: 'Maamul' },
      blurb: { en: 'Full control of the platform and its users.',
               so: 'Xakamayn buuxda oo nidaamka iyo isticmaalayaashiisa.' } },
    { key: 'management', label: { en: 'Management & Finance', so: 'Maareynta & Maaliyadda' },
      blurb: { en: 'Approve, audit and run the back office.',
               so: 'Ansixi, tijaabi oo wad xafiiska dambesa.' } },
    { key: 'field', label: { en: 'Field Operations', so: 'Howlaha Goobta' },
      blurb: { en: 'Mobile-first roles that work on the ground.',
               so: 'Doorka mobilada ku shaqeeya ee goobta shaqada.' } },
    { key: 'service', label: { en: 'Customer Service', so: 'Adeegga Macaamiisha' },
      blurb: { en: 'Serve customers through the Service Desk.',
               so: 'U adeeg macaamiisha adigoo maraya Service Desk.' } },
    { key: 'readonly', label: { en: 'Read-Only', so: 'Akhri Keliya' },
      blurb: { en: 'View-only access for oversight.',
               so: 'Aragti keliya oo la socodka.' } },
  ];

  // ---- capability catalog ------------------------------------------------
  // Coverage keys: 'web' | 'billing' | 'payments' | 'customers' | 'expenses' | 'hr' | 'assets' | 'sysadmin'
  private readonly defs: RoleDef[] = [
    {
      key: 'supersysadmin', tier: 'admin',
      title: { en: 'Super Sysadmin', so: 'Maamulaha Sare ee Nidaamka' },
      summary: {
        en: 'Root technical owner of the platform. Everything a System Administrator can do, plus the exclusive Superadmin page and the only unrestricted data scope in the system.',
        so: 'Milkiilaha farsamo ee nidaamka. Wax kasta oo Maamulaha Nidaamku sameyn karo, oo lagu daray bogga Superadmin ee gaarka ah iyo aragtida xogta ee aan xaddidnayn ee kaliya ee nidaamka.' },
      capabilities: [
        { en: 'Sees ALL branches by default and can switch to any single branch using the header branch selector — no other role can leave its own branch.', so: 'Wuxuu arkaa DHAAMMAAN laamaha, wuxuuna u gudbi karaa laan keliya isagoo isticmaalaya doorashada laanta ee madaxa — door kale lama baxsan karo laantiisa.' },
        { en: 'Full access to every web menu, page and dashboard.', so: 'Awood buuxda oo dhammaan menu-yada webka, bogagga iyo dashboard-yada.' },
        { en: 'Exclusive Superadmin page — an organisation-wide control panel hidden from every other role.', so: 'Bogga Superadmin ee gaarka ah — guddi maamul oo ururka oo dhan ah oo qarsoon doorka kale.' },
        { en: 'Access Control — assign or remove any role on any employee; grant or revoke per-employee permission switches (take payments, update meters, register customers).', so: 'Access Control — doro ama ka saar door kasta shaqaale kasta; siin ama joojin dammayaasha shaqaalaha (lacag-bixin, cusboonaysiin mitir, diiwaangelin macaamiil).' },
        { en: 'Date Change — correct bill dates and payment dates, e.g. record a payment against a closed/past billing cycle.', so: 'Date Change — sax taariikhda biilka iyo lacag-bixinta, tusaale: diiwaangeli lacag biil dameer ahayd (cycle xirnaa).' },
        { en: 'Session Management — view and terminate active user sessions; Audit Log — inspect every sensitive action.', so: 'Session Management — eeg oo jari casharrada firfircoon; Audit Log — fiiri ficil kasta oo xasaasi ah.' },
      ],
      limits: {
        en: 'No limits inside the application — treat as production root access and assign sparingly.',
        so: 'Xuduud ma jiro nidaamka gudihiisa — u arag sidii root access, siin shaqo kooban oo aammin ah.' },
      coverage: { web: 'full', billing: 'full', payments: 'full', customers: 'full', expenses: 'full', hr: 'full', assets: 'full', sysadmin: 'full' },
    },
    {
      key: 'sysadmin', tier: 'admin',
      title: { en: 'System Administrator', so: 'Maamulaha Nidaamka' },
      summary: {
        en: 'Day-to-day administrator of the whole web application. Controls who gets which role and fixes date/cycle problems that block payments.',
        so: 'Maamulaha maalinkasta ee web-ka oo dhan. Xukuma yaa heli doorka kee, wuxuuna saxaa dhibaatooyinka taariikhda/cycle-ka ee lacag-bixinta xanniba.' },
      capabilities: [
        { en: 'Full access to every web menu: Customers, Meters, Areas, Unit Rate, Employee Map, Billing, Finance, Expenses, HR, Service Desk, Assets and Inventory.', so: 'Awood buuxda oo dhammaan menu-yada: Macaamiisha, Mitirrada, Xaafadaha, Qiimaha Unugga, Khariidadda Shaqaalaha, Biilashada, Maaliyad, Kharashaadka, HR, Service Desk, Hantida iyo Bakhaarka.' },
        { en: 'Access Control — create and manage employee roles; switch individual permissions on/off per employee (e.g. can make payment, can update meter, can add customer).', so: 'Access Control — abuur oo maamul doorka shaqaalaha; dam/kar dammayaasha shaqaale kasta (tusaale: lacag-bixin, cusboonaysiin mitir, gelitaan macaamiil).' },
        { en: 'Date Change — correct a bill date or a payment date. Use it to post a payment on a past/closed cycle date when a customer paid late or a date was entered wrong.', so: 'Date Change — sax taariikhda biilka ama lacag-bixinta. Adeegso si aad u diiwaangeliso lacag taariikh dameer ahayd marka macaamiilku dambeeyay ama taariikh khaldan la geliyay.' },
        { en: 'Session Management — see who is logged in and force-logout any session.', so: 'Session Management — arag yaa galay oo ka saar cashar kasta.' },
        { en: 'Audit Log — trace who did what and when across the system.', so: 'Audit Log — raadiyee cidda wax samaysay iyo goorta ay sameysay nidaamka oo dhan.' },
        { en: 'This Roles & Capabilities page and the Payroll Guide are sysadmin-only.', so: 'Boggan Doorka & Awoodaha iyo Payroll Guide waa kuwo sysadmin keliya loo furan yahay.' },
      ],
      limits: {
        en: 'Sees only own branch data — cannot jump branches like supersysadmin. No access to the exclusive Superadmin page.',
        so: 'Wuxuu arkaa xogta laantiisa keliya — kama baxsan karo laamaha sida supersysadmin. Bogga Superadmin lama arko.' },
      coverage: { web: 'full', billing: 'full', payments: 'full', customers: 'full', expenses: 'full', hr: 'full', assets: 'full', sysadmin: 'full' },
    },
    {
      key: 'superadmin', tier: 'admin',
      title: { en: 'Super Admin (Operations)', so: 'Maamulaha Sare (Howlaha)' },
      summary: {
        en: 'Top operational role — runs billing and customer operations, including destructive bill actions that no other operational role holds.',
        so: 'Doorka ugu sareeya howlaha — wuxuu maamulaa biilashada iyo macaamiisha, oo ay ku jiraan ficillada halista ah ee biilka ee aan door kale oo howlaha lahayn.' },
      capabilities: [
        { en: 'All operational menus — Customers, Meters, Areas, Unit Rate, Employee Map, Billing, Finance, Expenses, HR, Service Desk, Assets, Inventory.', so: 'Dhammaan menu-yada howlaha — Macaamiisha, Mitirrada, Xaafadaha, Qiimaha Unugga, Khariidadda Shaqaalaha, Biilashada, Maaliyad, Kharashaadka, HR, Service Desk, Hantida, Bakhaarka.' },
        { en: 'Delete bills — permanent removal of a bill record; every deletion is written to the Audit Log.', so: 'Tirtir biilasha — tirtiir joogto ah oo diiwaanka biilka; tirtiir kasta waxaa lagu qoraa Audit Log.' },
        { en: 'Edit customer records and correct bill values/amounts.', so: 'Wax ka beddel diiwaanka macaamiisha oo sax qiimayaasha/lacagaha biilka.' },
        { en: 'Approve expenses and run end-to-end billing operations within own branch.', so: 'Ansixi kharashaadka oo wad howlaha biilashada ilaa dhammaad gudaha laantiisa.' },
        { en: 'Broad customer visibility — a dedicated superadmin customer query returns customers beyond normal filters.', so: 'Aragti macaamiil ballaaran — su\'aal gaar ah oo superadmin ayaa soo celisa macaamiisha ka baxsan shaandhadhka caadiga ah.' },
      ],
      limits: {
        en: 'No system-administration pages: no Access Control, no Session Management, no Audit Log, no Date Change. Scoped to own branch.',
        so: 'Ma jiraan bogagga maamulka nidaamka: ma jiro Access Control, Session Management, Audit Log, Date Change. Waxaa lagu xaddiday laantiisa.' },
      coverage: { web: 'full', billing: 'full', payments: 'full', customers: 'full', expenses: 'full', hr: 'partial', assets: 'full', sysadmin: 'none' },
    },
    {
      key: 'admin', tier: 'admin',
      title: { en: 'Admin (Operations)', so: 'Maamulaha (Howlaha)' },
      summary: {
        en: 'Branch-level operational administrator — same operational powers as Super Admin, restricted to the admin\'s own branch.',
        so: 'Maamulaha howlaha ee heer laan — awoodaha howlaha ee isku midka ah Maamulaha Sare, balse lagu xaddiday laanta maamulaha.' },
      capabilities: [
        { en: 'All operational menus (same list as Super Admin).', so: 'Dhammaan menu-yada howlaha (liiska isku midka ah Maamulaha Sare).' },
        { en: 'Delete bills and edit customer/bill records — the admin tier is the only one besides Super Admin holding destructive billing actions (audit-logged).', so: 'Tirtir biilasha oo wax ka beddel diiwaanka macaamiilka/biilka — heerka maamulka waa kan kaliya ee marka laga reebo Maamulaha Sare haysta ficillada halista ah ee biilka (waxaa lagu qoraa Audit Log).' },
        { en: 'Manage collectors and field staff within the branch; approve expenses.', so: 'Maamul uruuriyeyaasha iyo shaqaalaha goobta ee laanta gudahiisa; ansixi kharashaadka.' },
      ],
      limits: {
        en: 'Branch-restricted — sees only own branch. No system-administration pages (no Access Control / Sessions / Audit Log / Date Change).',
        so: 'Waxaa lagu xaddiday laantiisa — wuxuu arkaa laantiisa keliya. Ma jiraan bogagga maamulka nidaamka.' },
      coverage: { web: 'full', billing: 'full', payments: 'full', customers: 'full', expenses: 'full', hr: 'partial', assets: 'full', sysadmin: 'none' },
    },
    {
      key: 'finance', tier: 'management',
      title: { en: 'Finance', so: 'Maaliyad' },
      summary: {
        en: 'Controls money movement — bank channels, expenses, billing cycles and payroll cost reports.',
        so: 'Xukuma dhaqdhaqaaqa lacagta — kanaallada bangiga, kharashaadka, wareegyada biilashada iyo warbixinada kharashka mushaharka.' },
      capabilities: [
        { en: 'Finance menu — record and review bank transactions across all payment channels (Zaad, eDahab, Sahal, EBS, EFT, cash).', so: 'Menu-ga Maaliyad — diiwaangeli oo fiiri dhaqdhaqaaqa bangiga ee kanaallada oo dhan (Zaad, eDahab, Sahal, EBS, EFT, cash).' },
        { en: 'Open and close billing cycles together with supersysadmin.', so: 'Fur oo xir wareegyada biilashada wadajir la\'aan supersysadmin.' },
        { en: 'Expense dashboards and approval; Monthly Payroll Expense reports.', so: 'Dashboard-yada kharashaadka iyo ansixintooda; warbixinada Kharashka Mushaharka Bilaha.' },
        { en: 'Billing dashboards and financial reports across the branch.', so: 'Dashboard-yada biilashada iyo warbixinada maaliyadeed ee laanta.' },
      ],
      limits: {
        en: 'No employee/role administration, no destructive customer or bill actions. Branch-restricted.',
        so: 'Ma jirto maamulka shaqaalaha/doorka, mana jiraan ficillo halis ah oo macaamiil ama biil ah. Waxaa lagu xaddiday laantiisa.' },
      coverage: { web: 'partial', billing: 'full', payments: 'full', customers: 'partial', expenses: 'full', hr: 'partial', assets: 'partial', sysadmin: 'none' },
    },
    {
      key: 'auditor', tier: 'management',
      title: { en: 'Auditor', so: 'Hantidhowr' },
      summary: {
        en: 'Independent review — reads financial records and signs off in the expense approval chain without being able to change data.',
        so: 'Dib-u-eegis madaxbanaan — akhriya diiwaanka maaliyadeed oo saxeexa silsiladda ansixinta kharashaadka isagoo aan xogta beddeli karin.' },
      capabilities: [
        { en: 'Read bills, payments, customers and expense records for verification.', so: 'Akhri biilasha, lacag-bixinta, macaamiisha iyo diiwaanka kharashaadka si loo xaqiijiyo.' },
        { en: 'Act as the audit step in the expense approval chain (requester → approver → auditor).', so: 'Ka qaybqaado tallaabada hantidhowrka ee silsiladda ansixinta kharashaadka (requester → approver → auditor).' },
        { en: 'Access reports needed for reconciliation.', so: 'Gaadh warbixinada loo baahan yahay isbarbardhigga.' },
      ],
      limits: {
        en: 'Cannot create or modify records — review and sign-off only.',
        so: 'Ma abuuri ama beddeli karo diiwaan — dib-u-eegis iyo saxeex keliya.' },
      coverage: { web: 'partial', billing: 'partial', payments: 'partial', customers: 'full', expenses: 'full', hr: 'none', assets: 'partial', sysadmin: 'none' },
    },
    {
      key: 'approver', tier: 'management',
      title: { en: 'Expense Approver', so: 'Ansixiyaha Kharashaadka' },
      summary: {
        en: 'Approves or rejects expense requests raised by requesters — the decision gate before money leaves the organisation.',
        so: 'Ansixiya ama diida codsiyada kharashaadka ee soo gudbiya requester — albaabka go\'aanka ka hor inta uusan lacagtu ka bixin ururka.' },
      capabilities: [
        { en: 'Review pending expense requests and approve or reject them.', so: 'Dib-u-eeg codsiyada kharashaadka ee sugitaan, ansixi ama diid.' },
        { en: 'View expense dashboards and history for the branch.', so: 'Eeg dashboard-yada iyo taariikhda kharashaadka ee laanta.' },
      ],
      limits: {
        en: 'Cannot raise requests on behalf of others, cannot touch billing, payments or customer records.',
        so: 'Kama gudbin karo codsiyo magac kale, kama taaban karo biilashada, lacag-bixinta ama diiwaanka macaamiisha.' },
      coverage: { web: 'partial', billing: 'none', payments: 'none', customers: 'partial', expenses: 'full', hr: 'none', assets: 'none', sysadmin: 'none' },
    },
    {
      key: 'requester', tier: 'management',
      title: { en: 'Expense Requester', so: 'Codsadaha Kharashaadka' },
      summary: {
        en: 'Raises expense requests that flow to approver then auditor — cannot approve anything.',
        so: 'Soo gudbiya codsiyada kharashaadka ee u gudba approver ka dibna auditor — ma ansixin karo waxba.' },
      capabilities: [
        { en: 'Create expense requests (amount, category, receipt/attachment) and track their status.', so: 'Abuur codsiyada kharashaadka (lacag, qayb, rasiid/ku-lifaaq) oo la soco xaaladdooda.' },
      ],
      limits: {
        en: 'No approval rights, no operational menus.',
        so: 'Xuquuq ansixin ma jiro, menu-yo howlaha ma jiraan.' },
      coverage: { web: 'partial', billing: 'none', payments: 'none', customers: 'partial', expenses: 'full', hr: 'none', assets: 'none', sysadmin: 'none' },
    },
    {
      key: 'hr', tier: 'management',
      title: { en: 'HR', so: 'Maamulka Shaqaalaha' },
      summary: {
        en: 'Owns the employee lifecycle — records, attendance and the complete payroll suite.',
        so: 'Wuxuu leeyahay wareegga shaqaalaha — diiwaanka, timaamidda iyo xirmooyinka mushaharka oo dhan.' },
      capabilities: [
        { en: 'Employee profiles and records — create, view and maintain employee files.', so: 'Profaylka iyo diiwaanka shaqaalaha — abuur, eeg oo dayactir faylasha shaqaalaha.' },
        { en: 'Attendance tracking and reports.', so: 'La socodka timaamidda iyo warbixinadeeda.' },
        { en: 'Full payroll suite — payroll runs, payroll profiles, payslips, monthly payroll expense, employee form printing.', so: 'Xirmada mushaharka oo dhan — payroll runs, profiles, payslips, kharashka bilaha, daabacaadda foomka shaqaalaha.' },
      ],
      limits: {
        en: 'No billing, payment or finance operations. Branch-restricted. The Roles & Capabilities and Access Control pages remain sysadmin-only.',
        so: 'Ma jiraan howlaha biilashada, lacag-bixinta ama maaliyadda. Waxaa lagu xaddiday laantiisa. Bogagga Roles & Access Control waa sysadmin keliya.' },
      coverage: { web: 'partial', billing: 'none', payments: 'none', customers: 'partial', expenses: 'partial', hr: 'full', assets: 'none', sysadmin: 'none' },
    },
    {
      key: 'collector', tier: 'field',
      title: { en: 'Collector', so: 'Uruuriye' },
      summary: {
        en: 'Mobile-app field role — reads meters, creates bills and collects payments for the customers on their route/branch.',
        so: 'Doorka goobta ee app-ka mobilada — akhriya mitirrada, abuura biilasha oo uruuriya lacag-bixinta macaamiisha waddadiisa/laantiisa.' },
      capabilities: [
        { en: 'Sees only the customers assigned to them / in their branch inside the mobile app.', so: 'Wuxuu arkaa macaamiisha loo qoondeeyay / kuwa laantiisa kaliya gudaha app-ka mobilada.' },
        { en: 'Enter meter readings and generate bills on the spot.', so: 'Geli akhriska mitirka oo abuur biilka isla markiiba.' },
        { en: 'Collect payments (EVC Plus / bank / cash) and issue receipts — only while the payment permission switch is ON for their account; a sysadmin can disable it instantly from Access Control.', so: 'Uruuri lacag-bixinta (EVC Plus / bangi / cash) oo bixi rasiid — kaliya marka dammaha lacag-bixintu furan yahay akoonkooda; sysadmin wuxuu isla markiiba ka joojin karaa Access Control.' },
        { en: 'Offline queue — readings taken without connection sync when back online.', so: 'Safka offline — akhrisyada la qaatay iyadoo aan isku-xidhna jirin waa la isku xidh-dhaafiyaa marka internetku noqdo.' },
      ],
      limits: {
        en: 'No web menus, no back office. Cannot take payments when the payment permission is off; sees only own branch.',
        so: 'Menu-yo web ma jiraan, xafiis dambe ma jiro. Lacag-bixin ma sameyn karo marka dammah daman yahay; wuxuu arkaa laantiisa keliya.' },
      coverage: { web: 'none', billing: 'full', payments: 'partial', customers: 'full', expenses: 'none', hr: 'none', assets: 'none', sysadmin: 'none' },
    },
    {
      key: 'payments', tier: 'field',
      title: { en: 'Payments', so: 'Lacag-bixin' },
      summary: {
        en: 'Dedicated payment-entry role — posts customer payments and account uploads without wider billing powers.',
        so: 'Door gaar ah oo gelitaanka lacag-bixinta — diiwaangeliya lacag-bixinta macaamiisha iyo uploads xisaabka iyadoo aan awood biilasha oo ballaaran lahayn.' },
      capabilities: [
        { en: 'Record payments against bills (including bulk account uploads).', so: 'Diiwaangeli lacag-bixinta biilasha (oo ay ku jiraan uploads xisaabka oo badan).' },
      ],
      limits: {
        en: 'Payments only — cannot create bills, edit customers or approve expenses.',
        so: 'Lacag-bixin keliya — ma abuuri karo biilo, ma beddeli karo macaamiil, mana ansixin karo kharash.' },
      coverage: { web: 'partial', billing: 'partial', payments: 'full', customers: 'partial', expenses: 'none', hr: 'none', assets: 'none', sysadmin: 'none' },
    },
    {
      key: 'meterupdate', tier: 'field',
      title: { en: 'Meter Update', so: 'Cusboonaysiinta Mitirka' },
      summary: {
        en: 'Narrow mobile role — updates meter information only.',
        so: 'Door mobilada oo dhuuban — wuxuu cusboonaysiiyaa xogta mitirka keliya.' },
      capabilities: [
        { en: 'Update meter readings/details in the mobile app.', so: 'Cusboonaysii akhriska/faahfaahinta mitirka app-ka mobilada.' },
      ],
      limits: {
        en: 'No billing, payments or customer editing.',
        so: 'Biilashada, lacag-bixinta ama beddelka macaamiil ma jiraan.' },
      coverage: { web: 'none', billing: 'partial', payments: 'none', customers: 'partial', expenses: 'none', hr: 'none', assets: 'none', sysadmin: 'none' },
    },
    {
      key: 'addnewcustomer', tier: 'field',
      title: { en: 'Add New Customer', so: 'Diiwaangelinta Macaamiilka Cusub' },
      summary: {
        en: 'Mobile registration role — enrols new customers from the field.',
        so: 'Doorka diiwaangelinta mobilada — wuxuu goobta ka diiwaangeliyaa macaamiil cusub.' },
      capabilities: [
        { en: 'Register new customers (name, phone, area, meter) from the mobile app.', so: 'Diiwaangeli macaamiil cusub (magac, taleefan, xaafad, mitir) app-ka mobilada.' },
      ],
      limits: {
        en: 'Registration only — cannot bill, collect payments or edit existing customers.',
        so: 'Diiwaangelin keliya — biil ma abuuro, lacag ma uruuriyo, mana beddelo macaamiil jira.' },
      coverage: { web: 'none', billing: 'none', payments: 'none', customers: 'full', expenses: 'none', hr: 'none', assets: 'none', sysadmin: 'none' },
    },
    {
      key: 'engineer', tier: 'field',
      title: { en: 'Engineer (Kormeer)', so: 'Injineer (Kormeer)' },
      summary: {
        en: 'Field inspection — verifies connections, meters and area data on the ground.',
        so: 'Kormeerka goobta — wuxuu xaqiijiyaa isku-xidhka, mitirrada iyo xogta xaafadda dhulka.' },
      capabilities: [
        { en: 'Inspect customer connections and meters; view the employee/customer map.', so: 'Kormeer isku-xidhka macaamiisha iyo mitirrada; eeg khariidadda shaqaalaha/macaamiisha.' },
        { en: 'Report field findings back to operations.', so: 'U soo gudbi natiijooyinka goobta howlaha.' },
      ],
      limits: {
        en: 'Inspection scope — no billing, payments or expense rights.',
        so: 'Kormeer keliya — biilashada, lacag-bixinta ama xuquuqda kharashaadka ma jiraan.' },
      coverage: { web: 'partial', billing: 'partial', payments: 'none', customers: 'partial', expenses: 'none', hr: 'none', assets: 'partial', sysadmin: 'none' },
    },
    {
      key: 'kormeer', tier: 'field',
      title: { en: 'Kormeer (Surveyor)', so: 'Kormeer' },
      summary: {
        en: 'Legacy field-survey role — same inspection scope as Engineer. Kept for existing accounts; prefer Engineer for new assignments.',
        so: 'Doorka hore ee kormeerka goobta — isku nooc ahaan la mid ah Engineer. Loo haystaa akoonnada jira; isticmaal Engineer kuwa cusub.' },
      capabilities: [
        { en: 'Field inspection of connections and meters (legacy mobile flow).', so: 'Kormeerka goobta ee isku-xidhka iyo mitirrada (qorshaha hore ee mobilada).' },
      ],
      limits: {
        en: 'Same limits as Engineer — no billing or payment powers.',
        so: 'Isla xuduudaha Engineer — biilashada iyo lacag-bixinta ma jiraan.' },
      coverage: { web: 'partial', billing: 'partial', payments: 'none', customers: 'partial', expenses: 'none', hr: 'none', assets: 'partial', sysadmin: 'none' },
    },
    {
      key: 'customercare', tier: 'service',
      title: { en: 'Customer Care', so: 'Daryeelka Macaamiisha' },
      summary: {
        en: 'Front-line Service Desk — logs complaints and looks up customer records to answer queries.',
        so: 'Service Desk-ka safka hore — diiwaangeliya cabashada oo raadiya diiwaanka macaamiisha si loo jawaabo su\'aalaha.' },
      capabilities: [
        { en: 'Log and handle customer complaints in the Service Desk (Complaints dashboard).', so: 'Diiwaangeli oo qabso cabashada macaamiisha Service Desk-ka (dashboard-ka cabashada).' },
        { en: 'Look up customer records, bill history and payment history to resolve queries.', so: 'Raadi diiwaanka macaamiilka, taariikhda biilka iyo lacag-bixinta si loo xalliyo su\'aalaha.' },
      ],
      limits: {
        en: 'No financial operations — cannot post payments, change bills or approve expenses.',
        so: 'Howlaha maaliyadeed ma jiraan — lacag ma diiwaangeliyo, biil ma beddelo, kharash ma ansixiyo.' },
      coverage: { web: 'partial', billing: 'partial', payments: 'none', customers: 'full', expenses: 'none', hr: 'none', assets: 'none', sysadmin: 'none' },
    },
    {
      key: 'customerservice', tier: 'service',
      title: { en: 'Customer Service', so: 'Adeegga Macaamiisha' },
      summary: {
        en: 'Service Desk with broader record access — handles complaints end-to-end and maintains customer contact details.',
        so: 'Service Desk oo aragti diiwaan ballaaran leh — wuxuu cabashada u qabsadaa ilaa dhammaad, wuxuuna dayactiraa faahfaahinta xiriirka macaamiisha.' },
      capabilities: [
        { en: 'Everything Customer Care can do, plus fuller customer-record maintenance.', so: 'Wax kasta oo Customer Care sameyn karo, oo lagu daray dayactir diiwaanka macaamiilka oo ballaaran.' },
        { en: 'Included in the backend allowed-roles for customer operations alongside sysadmin/superadmin.', so: 'Wuxuu ku jiraa allowed-roles backend-ka ee howlaha macaamiisha oo ay la socdaan sysadmin/superadmin.' },
      ],
      limits: {
        en: 'No billing administration or financial approvals.',
        so: 'Ma jirto maamulka biilashada ama ansixinta maaliyadeed.' },
      coverage: { web: 'partial', billing: 'partial', payments: 'none', customers: 'full', expenses: 'none', hr: 'none', assets: 'none', sysadmin: 'none' },
    },
    {
      key: 'readonly', tier: 'readonly',
      title: { en: 'Read-Only Viewer', so: 'Daawade Akhri' },
      summary: {
        en: 'Oversight without write access — dashboards and reports only.',
        so: 'La socod iyadoo aan awood qorid lahayn — dashboards iyo warbixinno keliya.' },
      capabilities: [
        { en: 'View dashboards and reports (dashboards, bill reports).', so: 'Eeg dashboards iyo warbixinno (dashboards, warbixinada biilasha).' },
      ],
      limits: {
        en: 'Strictly read-only — no create, update, delete, payments or approvals anywhere.',
        so: 'Akhri keliya si adag — abuurid, cusboonaysiin, tirtiirid, lacag-bixin ama ansixin meelna ma jiraan.' },
      coverage: { web: 'partial', billing: 'partial', payments: 'none', customers: 'partial', expenses: 'none', hr: 'none', assets: 'none', sysadmin: 'none' },
    },
    {
      key: 'viewer', tier: 'readonly',
      title: { en: 'Viewer', so: 'Daawade' },
      summary: {
        en: 'Alias of the read-only role kept for backwards compatibility.',
        so: 'Magac kale oo doorka akhriga keliya ah oo loo haysto iswaafaqnimada hore.' },
      capabilities: [
        { en: 'View dashboards and reports.', so: 'Eeg dashboards iyo warbixinno.' },
      ],
      limits: {
        en: 'Same as Read-Only — no write access anywhere.',
        so: 'Isku mid ah Akhriga Keliya — awood qorid ma jirto meelna.' },
      coverage: { web: 'partial', billing: 'partial', payments: 'none', customers: 'partial', expenses: 'none', hr: 'none', assets: 'none', sysadmin: 'none' },
    },
  ];

  constructor(private apiService: ApiService, private langService: LanguageService) {}

  get lang(): AppLanguage {
    return this.langService.current;
  }

  setLang(l: AppLanguage): void {
    this.langService.setLanguage(l);
  }

  /** Pick the supersysadmin-aware scope note. */
  get branchScopeNote(): string {
    const superUser = sessionStorage.getItem('issupersysadmin') === 'true'
      || sessionStorage.getItem('userRoles')?.toLowerCase().includes('supersysadmin');
    return (superUser ? this.ui['scopeNoteSuper'] : this.ui['scopeNoteBranch'])[this.lang];
  }

  ngOnInit(): void {
    forkJoin({
      rolesRes: this.apiService.getRolesDetails(),
      empRes: this.apiService.getEmployees(),
    }).subscribe({
      next: ({ rolesRes, empRes }) => {
        const roles = rolesRes?.roles || [];
        const employees = empRes?.employees || [];
        this.totalEmployees = employees.length;
        this.rows = this.buildRows(roles, employees);
        this.loading = false;
      },
      error: () => {
        this.loadError = 'Failed to load roles.';
        this.loading = false;
      },
    });
  }

  /** Employees grouped per role name — already branch-scoped by the backend. */
  private buildRows(roles: any[], employees: any[]): RoleRow[] {
    const byRole = new Map<string, string[]>();
    for (const emp of employees) {
      const name = emp.employeeName || 'Unnamed';
      for (const r of emp.roles || []) {
        const key = (r.roleName || '').toLowerCase();
        if (!key) { continue; }
        if (!byRole.has(key)) { byRole.set(key, []); }
        byRole.get(key)!.push(name);
      }
    }

    const rows: RoleRow[] = this.defs.map((d) => ({
      ...d,
      memberCount: (byRole.get(d.key) || []).length,
      members: (byRole.get(d.key) || []).sort(),
    }));

    // Any live DB role without a catalog entry still shows up as a placeholder.
    for (const r of roles) {
      const key = (r.roleName || '').toLowerCase();
      if (!key || this.defs.some((d) => d.key === key)) { continue; }
      rows.push({
        key,
        title: { en: r.roleName, so: r.roleName },
        summary: {
          en: 'Custom role — capabilities not yet documented in this guide.',
          so: 'Door gaar ah — awoodihiisa weli laguma caddeyn hagahan.' },
        capabilities: [],
        limits: {
          en: 'Not documented.',
          so: 'Lama caddeyn.' },
        tier: 'service',
        coverage: {},
        memberCount: (byRole.get(key) || []).length,
        members: (byRole.get(key) || []).sort(),
      });
    }
    return rows;
  }

  rowsOfTier(tier: Tier): RoleRow[] {
    return this.rows.filter((r) => r.tier === tier);
  }

  tierLabelOf(tier: Tier): L {
    return this.tierMeta.find((t) => t.key === tier)?.label
      || { en: tier, so: tier };
  }

  coverageClass(row: RoleRow, domain: string): string {
    return 'cov-' + (row.coverage[domain] || 'none');
  }

  coverageMark(row: RoleRow, domain: string): string {
    const c = row.coverage[domain] || 'none';
    return c === 'full' ? '●' : c === 'partial' ? '◐' : '—';
  }
}
